import {DatasheetParameterGroup, InstalledFanDatasheet} from "../../business/InstalledFanDatasheet";
import React, {ReactNode, useContext, useState} from "react";
import {ParamCard, ParamCardTr} from "../../components/domain/SimulationDatasheet";
import {OctavesSplBarChart} from "../../components/domain/OctavesSplBarChart";
import {Card} from "../../components/Card";
import {createInternationalizationPrefixer, getCurrentLanguage, resolveIstring, resolveParameterValue} from "../../platform/i18n/i18n";
import {Summaries} from "../../components/Summaries";
import {PressureChart} from "../../components/domain/PressureChart";
import {PowerCurvesChart} from "../../components/domain/PowerCurvesChart";
import {FanEfficiencyChart} from "../../components/domain/FanEfficiencyChart";
import {FanTorqueChart} from "../../components/domain/FanTorqueChart";
import {DimensionalImg} from "../../components/domain/DimensionalImg";
import {LATEST_OPTISEL_VERSION} from "../../components/Changelog";
import {LegacyLocatedSplChart} from "../../charting/LegacyLocatedSplChart";
import {ResonanceCampbellChart} from "../../components/domain/ResonanceCampbellChart";
import {ImpellerGeometryTable} from "../../components/assembly/ImpellerGeometryTable";
import {HotIssueContext} from "../issues/hotssue/HotIssueContextProvider";
import {OrderViaDatasheetButtonWithEmbeddedModal} from "../order/useOrderInquiryForMemoizedDatasheet";
import {Protected, ProtectedByOneOf} from "../../layout/Protected";
import AjaxService from "../../platform/ajax/AjaxService";


const t = createInternationalizationPrefixer("InstalledFanDatasheet"); // reused SimulationDatasheet

export type ParamsForInteractiveLocatedSplChart = {
    housingDiameterMilimeter: number,
    pwlTot: number,
    operation: "INDUCED_DRAFT" | "FORCED_DRAFT"

}

export function DatasheetComponent({
                                       datasheet,
                                       memoizedDatasheetId,
                                       blueprintId,
                                       paramsForInteractiveLocatedSplChart,
                                       showPrintCommentInput = true,
                                       hideDimensionsApprovalBanner = false
                                   }: {
    datasheet: InstalledFanDatasheet,
    memoizedDatasheetId?: string,
    blueprintId?: string,
    paramsForInteractiveLocatedSplChart: ParamsForInteractiveLocatedSplChart,
    showPrintCommentInput?: boolean,
    hideDimensionsApprovalBanner?: boolean
}): JSX.Element {

    const [commentForPrint, setCommentForPrint] = useState<string>("");
    const [printing, setPrinting] = useState<boolean>(false);
    const {currentHotIssueSummary} = useContext(HotIssueContext);

    const pdfExportLinkAutolinkEnhancement = currentHotIssueSummary == null ? "" : "&assignIssueId=" + currentHotIssueSummary.id;

    const downloadPdf = async () => {
        const url = "/api/simulation/pdf-export/" + memoizedDatasheetId + "?appVersion=" + LATEST_OPTISEL_VERSION
            + "&comment=" + encodeURIComponent(commentForPrint) + "&lang=" + getCurrentLanguage() + pdfExportLinkAutolinkEnhancement;
        setPrinting(true);
        try {
            await AjaxService.downloadAuthenticatedFile(url, "DS_WENTECH.pdf");
        } catch (error: any) {
            if (error?.message === "expired") {
                window.alert(t("Karta wygasła - uruchom ponownie dobór i spróbuj jeszcze raz."));
            } else {
                window.alert(t("Nie udało się pobrać karty wentylatora."));
            }
        } finally {
            setPrinting(false);
        }
    };

    const downloadDwBomFile = async (url: string, filename: string) => {
        try {
            await AjaxService.downloadAuthenticatedFile(url, filename);
        } catch (error) {
            window.alert(t("Nie udało się pobrać pliku."));
        }
    };

    const bepEfficiencyPoint = datasheet.globalBestEfficiencyEfficiencyPoint ?? undefined;
    const bepPowerPoint = datasheet.globalBestEfficiencyPowerPoint ?? undefined;

    return <div className={"row"}>
        <div className={"col-12 mb-3"}>
            <div className={"text-center mt-3 mb-5"}>
                <h2>{t("Karta danych technicznych wentylatora")}</h2>
            </div>
            {datasheet.mechanicalResultDatasheetParameters === null && <div className={"alert alert-warning mt-5 fw-boldest fs-3"}><i
                className="fas fa-exclamation-circle"></i> {t('Brak rozwiązania mechanicznego dla zadanych parametrów')}</div>}
            {!hideDimensionsApprovalBanner && datasheet.mechanicalResultDatasheetParameters !== null && (datasheet.wymiaryPrzylaczenioweZatwierdzone
                ? <div className={"alert alert-success mt-3"}><i className="fas fa-check-circle"></i> {t("Wymiary mechaniczne zatwierdzone")}</div>
                : <div className={"alert alert-warning mt-3"}><i className="fas fa-exclamation-triangle"></i> {t("Wymiary mechaniczne niezatwierdzone")}</div>)}
            <Card>
                <div className={"d-flex"}>

                    <div className={"flex-grow-1"}>
                        <div className={"fs-7"}>{resolveIstring(datasheet.fanGeneralFanInformation.applicationTypeName)}</div>
                        <a className="text-gray-800 text-hover-primary fs-2 fw-bolder" href="#/">
                            {datasheet.fanGeneralFanInformation.fanName}
                        </a>
                        <div className={"fs-6"}>{resolveIstring(datasheet.fanGeneralFanInformation.manufacturingDescription)}</div>
                        <div className="d-flex flex-wrap fw-bold mb-4 fs-7 text-gray-600">{resolveIstring(datasheet.fanGeneralFanInformation.fanWorkingPointDescription)}</div>
                    </div>
                    <Summaries>
                        {datasheet.importantParameters.map(importantParam =>
                            <Summaries.Box
                                key={importantParam.identifier}
                                label={resolveIstring(importantParam.name) + (importantParam.unit === "-" || importantParam.unit.length === 0 ? "" : " [" + importantParam.unit + "]")} value={importantParam.value}/>)}

                        {memoizedDatasheetId !== undefined && <div className="d-flex mb-4">
                            <Protected privilege={"PERM_DW_AND_BOM"}>
                                {blueprintId && <>
                                    <button className={"btn btn-sm btn-secondary me-2 ps-2 pe-2"} onClick={() => downloadDwBomFile(`/api/impeller-assembly-xls-export/${blueprintId}`, "DW_WENTECH.xlsx")}>
                                        <div className={"mb-1 mt-2"}><i className="fas fa-download fs-2"></i></div>
                                        DW
                                    </button>
                                    <button className={"btn btn-sm btn-secondary me-2 ps-2 pe-2"} onClick={() => downloadDwBomFile(`/api/impeller-assembly-xls-export/${blueprintId}/workaround`, "DW_WENTECH_plaski.xlsx")}>
                                        <div className={"mb-1 mt-2"}><i className="fas fa-download fs-2"></i></div>
                                        DW TXT
                                    </button>
                                    <button className={"btn btn-sm btn-secondary me-2 ps-2 pe-2"} onClick={() => downloadDwBomFile(`/api/export-bill-of-materials-xlsx/${blueprintId}`, "BOM_WENTECH.xlsx")}>
                                        <div className={"mb-1 mt-2"}><i className="fas fa-download fs-2"></i></div>
                                        BOM
                                    </button>
                                </>}
                            </Protected>
                            <ProtectedByOneOf privileges={["PERM_SELECTION", "PERM_FLOW_SIMULATION"]}>
                                <button className="btn btn-sm btn-primary me-3" disabled={printing} onClick={downloadPdf}>
                                    <div className={"mb-1 mt-2"}><i className={printing ? "fas fa-spinner fa-spin fs-1" : "far fa-file-pdf fs-1"}></i></div>
                                    {t("Wydruk")}</button>
                            </ProtectedByOneOf>
                            <Protected privilege={"PERM_SELECTION"}>
                                <OrderViaDatasheetButtonWithEmbeddedModal memoizedDatasheetId={memoizedDatasheetId}/>
                            </Protected>
                        </div>}
                    </Summaries>
                </div>
            </Card>
        </div>

        <div className={"col-12 mb-3"}>
            <Card>
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">{t("Charakterystyka przepływowa")}</span>
                </h3>
                <PressureChart
                    mainPressureCurve={datasheet.fanStaticPressureCurve}
                    workingPointToMark={datasheet.fanStaticPressureWorkingPoint}
                    alternativeCurves={datasheet.angleVariantFanStaticPressureCurves}
                    mainBladeAngleFormatted={datasheet.importantParameters.filter(p => p.identifier === 'requestedBladeAngle')[0].value}
                    angleFanBestEfficiencyPoint={datasheet.angleFanBestEfficiencyPoint}
                    apiMarginDefiningPoint={datasheet.apiMarginDefiningPoint}
                    currentAngleReservesDefiningPoint={datasheet.currentAngleReservesDefiningPoint}

                    globalBestEfficiencyPressurePoint={datasheet.globalBestEfficiencyPressurePoint ?? undefined}
                />
            </Card>
        </div>

        <div className={"col-12 mb-3"}>
            <Card>
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">{t("Charakterystyka mocy")}</span>
                </h3>
                <PowerCurvesChart
                    powerWorkingPoint={datasheet.fanPowerWorkingPoint}
                    powerCurve={datasheet.fanPowerCurve}
                    allAngleVariantsPowerCurves={datasheet.angleVariantFanPowerCurves}
                    mainBladeAngleFormatted={datasheet.importantParameters.filter(p => p.identifier === 'requestedBladeAngle')[0].value}
                    globalBepPoint={bepPowerPoint}/>
            </Card>
        </div>

        <div className={"col-12 mb-3"}>
            <Card>
                <h3 className="card-title align-items-start flex-column">
                    <span className="card-label fw-bolder fs-3 mb-1">{t("Charakterystyka sprawności statycznej")}</span>
                </h3>
                <FanEfficiencyChart
                    efficiencyCurve={datasheet.installationStaticEfficiencyCurve}
                    allAngleVariantsEfficiencyCurves={datasheet.angleVariantEfficiencyCurves}
                    workingPoint={datasheet.installationStaticEfficiencyWorkingPoint}
                    mainBladeAngleFormatted={datasheet.importantParameters.filter(p => p.identifier === 'requestedBladeAngle')[0].value}
                    globalBepPoint={bepEfficiencyPoint}/>
            </Card>
        </div>

        <div className={"col-6"}>
            {parameterGroupToParamCard(datasheet.parameterGroups[0])}
            {parameterGroupToParamCard(datasheet.parameterGroups[1])}
            {parameterGroupToParamCard(datasheet.parameterGroups[2])}
        </div>
        <div className={"col-6"}>
            {datasheet.parameterGroups[4] !== undefined && parameterGroupToParamCard(datasheet.parameterGroups[4])}

            {datasheet.parameterGroups[3] !== undefined && parameterGroupToParamCard(datasheet.parameterGroups[3])}
            <Card additionalClassNames={"card mb-6 mb-xl-9 mt-1"}>
                <h5>{t("Moc akustyczna dB(A)")}</h5>
                <OctavesSplBarChart octavesSpl={datasheet.octavesPwls.filter(o => o.octaveFrequency > 32 && o.octaveFrequency < 15000)}
                                    octaveSplExtractor={spl => spl.pwlDba}
                                    totalValue={parseFloat(datasheet.workingPointPwlTot.value)}
                                    unitName={"dB(A)"}/>
                <h6>{t("Moc akustyczna dB(Lin)")}</h6>
                <OctavesSplBarChart octavesSpl={datasheet.octavesPwls.filter(o => o.octaveFrequency > 32 && o.octaveFrequency < 15000)}
                                    octaveSplExtractor={spl => spl.pwlLin}
                                    totalValue={parseFloat(datasheet.workingPointPwlTotLin.value)}
                                    unitName={"dB"}/>
                <p className={"pt-3 fs-7 text-center"}>
                    {t('Widmo akustyczne wirnika nie uwzględnia interferencji, odbić oraz innych urządzeń działających w tle.')}
                </p>
            </Card>

        </div>

        <div className={"col-6"}>
            <Card header={t("Charakterystyka momentu")} subHeader={t("Moment obrotowy w funkcji obrotów wirnika")} additionalClassNames={"card mb-6 mb-xl-9 mt-1"}>
                <FanTorqueChart torqueCurve={datasheet.fanTorqueCurve}/>
            </Card>
        </div>
        {datasheet.mechanicalResultDatasheetParameters != undefined && <div className={"col-6 mb-5"}>
            <Card header={t("Charakterystyka rezonansowa")}>
                {datasheet.mechanicalResultDatasheetParameters.resonanceReport.estimated && <>
                    <div className={"alert alert-warning"}><i className="fas fa-exclamation-triangle"></i> {t("Wartości rezonansów estymowane!")}</div>
                </>}
                {datasheet.mechanicalResultDatasheetParameters.resonanceReport.bpfResonating && <>
                    <div className={"alert alert-danger"}><i className="fas fa-exclamation-circle"></i> {t("Wykryto rezonans BPF!")}</div>
                </>}
                <ResonanceCampbellChart
                    resonanceCurve={datasheet.mechanicalResultDatasheetParameters.resonanceReport.resonanceCurve}
                    workingPointResonances={datasheet.mechanicalResultDatasheetParameters.resonanceReport.workingPointResonances}/>
            </Card>
        </div>}

        <div className={"col-12 mb-5"}>
            <Card>
                <LegacyLocatedSplChart
                    operation={paramsForInteractiveLocatedSplChart.operation == "INDUCED_DRAFT" ? "induced-ssacy" : "forced-tloczacy"}
                    housingDiameterMilimeter={paramsForInteractiveLocatedSplChart.housingDiameterMilimeter}
                    pwlTot={paramsForInteractiveLocatedSplChart.pwlTot}/>
            </Card>
        </div>


        {datasheet.mechanicalResultDatasheetParameters != undefined && <div className={"col-12 mb-5"}>
            <Card header={t("Geometria")}>
                <ImpellerGeometryTable geometryReport={datasheet.mechanicalResultDatasheetParameters.geometryReport}
                                       variant={"full-table"}/>
            </Card>
        </div>}

        <div className={"col-12"}>
            {datasheet.dimensionalImageDescriptors.length > 0 && <Card header={t("Wymiary montażowe")} additionalClassNames={"min-h-500px"}>
                <div className={"row"}>
                    {datasheet.dimensionalImageDescriptors.map((descriptor, idx) =>
                        <div key={descriptor.templateName} className={(idx == 0 ? "col-12" : "col-6 mt-20")}>
                            <Protected privilege={"PERM_DEVELOPMENT_TOOLS"}>
                                <div className={"p-1 bg-gray-100"}>
                                    <div>(wybór rysunku - informacja dla administratora)</div>
                                    <div className={"text-gray-400 fw-bolder"}><strong style={{color: 'black'}}>{descriptor.templateName}</strong> <small>{descriptor.dynamicLabelValues?.configNameWithProfileForDebug?.pl + ""}</small></div>
                                </div>
                            </Protected>
                            <DimensionalImg descriptor={descriptor}/>
                        </div>)}
                </div>
            </Card>}
        </div>

        {showPrintCommentInput && <Card header={t("Komentarz")} additionalClassNames={"mt-3"}>
            <textarea className={"w-100"}
                      onChange={e => setCommentForPrint(e.target.value)} value={commentForPrint}/>
        </Card>}


    </div>

}


function parameterGroupToParamCard(parameterGroup: DatasheetParameterGroup): ReactNode {
    return <ParamCard
        key={parameterGroup.name.en}
        header={resolveIstring(parameterGroup.name)}
        subheader={resolveIstring(parameterGroup.description)}
        trs={parameterGroup.parameters.map(p => <ParamCardTr key={p.name.en} name={resolveIstring(p.name)} unit={p.unit} value={resolveParameterValue(p.value)} estimated={p.estimated}/>)}/>
}